import useTranslation from 'next-translate/useTranslation'
import { FC } from 'react'
import { FlexboxGrid } from 'rsuite'
import { useRouter } from 'next/router'

import { useAuth } from '../../services/useAuth'
import { formatPriceNumber } from '../../utils/math'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import { getSupplierDeliveryDetails, isFreeDelivery } from '../../lib/service'
import {
  getDeliveryEstimation,
  getIsOverExactStock,
  getProductCurrency,
  getProductEstimatedDeliveryText,
  getStockLimitStatus,
  hasStockBackDate,
  StockLimitStatus,
} from '../../lib/product'
import { Product, ProductQuantity, Supplier } from '../../utils/types/Product'
import { formatTimeInterval } from '../../lib/time'
import ShippingIcon from '../Icons/ShippingIcon'
import useIsOnMobile from '../../services/useIsOnMobile'

import styles from '../../styles/ProductShippingInfo.module.less'

interface ProductShippingInfoProps {
  product: Product
  supplier?: Supplier
  productQuantity: ProductQuantity[]
}

const ProductShippingInfo: FC<ProductShippingInfoProps> = (props) => {
  const { product, supplier, productQuantity } = props

  const { t } = useTranslation('products')
  const { locale: countryAndLocale } = useRouter()
  const isOnMobile = useIsOnMobile()
  const { isVatIncluded } = useAuth()

  const { country, locale } = getCountryAndLocaleStrings(countryAndLocale)
  const currency = getProductCurrency(product, country)

  const {
    freeShippingLimit: freeLimit,
    deliveryTime,
    deliveryCost,
  } = getSupplierDeliveryDetails(supplier, country, isVatIncluded)

  const isFreeDeliveryAvailable = isFreeDelivery(product, country, isVatIncluded)
  const freeShippingLimit = formatPriceNumber(freeLimit)
  const overExactStockProduct = productQuantity
    .find((prod) => getIsOverExactStock(prod))

  const overManualStockProduct = productQuantity
    .find((prod) => Number(prod.quantity) > Number(prod.manualStockLimit))

  const hasAddedQuantityWithoutSbd = productQuantity
    .some((ele) => ele.exactStockLevel !== null
        && Number(ele.quantity) > Number(ele.exactStockLevel)
        && !hasStockBackDate(ele.stockDate))

  const getSubHeading = () => (
    <b>
      {isFreeDeliveryAvailable
        ? t('Free delivery')
        : t('Free shipping for orders over {{price}} {{currency}}', { price: freeShippingLimit, currency })}
    </b>
  )

  const renderStockLimitDeliveryNotice = () => {
    const stockLimitStatus = getStockLimitStatus(overExactStockProduct)

    const SbdVariant = productQuantity.find((item) => {
      const limitStatus = getStockLimitStatus(item)
      return limitStatus === 'HAS_STOCK_BACK_DATE'
    })

    if (SbdVariant) {
      return `${t('Est delivery {{deliveryDate}} for in-stock items', { deliveryDate: getDeliveryEstimation(
        product,
        country,
        locale,
      ) })} - ${t('for items exceeding stock, check back after restock')}`
    }

    // Quantity Exceeds Exact Stock With SBD
    if (stockLimitStatus === StockLimitStatus.HAS_STOCK_BACK_DATE && !hasAddedQuantityWithoutSbd) {
      return `${t('The selected product is not in stock, new estimated delivery {{date}}', { date:
          formatTimeInterval(overExactStockProduct?.timeframe!, overExactStockProduct?.stockDate!, undefined, locale) })}. ${t('The selected quantity will be confirmed with the supplier')}.`
    }

    // Quantity Exceeds Exact Stock Without SBD
    if (stockLimitStatus === StockLimitStatus.EXCEEDS_EXACT_STOCK || hasAddedQuantityWithoutSbd) {
      return `${t('The selected quantity is not in stock')}. ${t('Lower quantity or ask for other products that are in stock')}.`
    }

    // Quantity Exceed Manual Stock Limit but within Exact Stock
    if (overManualStockProduct?.id && !overExactStockProduct?.id) {
      return `${t('Estimated delivery time is {{days}} working days', {
        days: deliveryTime,
      })}. ${t('The selected quantity will be confirmed with the supplier')}`
    }

    return getProductEstimatedDeliveryText(
      product,
      country,
      t,
      locale,
    )
  }

  return (
    <FlexboxGrid className="flex-flow-row">
      <FlexboxGrid.Item>
        <ShippingIcon />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item className="flex-shrink-col-dynamic margin-left-spacer">
        {getSubHeading()}
        <div className={styles['delivery-text']}>
          <span data-testid="product-delivery-text">
            {renderStockLimitDeliveryNotice()}
          </span>
          {!isOnMobile || renderStockLimitDeliveryNotice().length < 40 ? (
            <span className={styles['supplier-fee']}>
              {isFreeDeliveryAvailable ? t('Product has free shipping')
                : t('{{price}} shipping per order', { price: `${formatPriceNumber(deliveryCost)} ${currency}` })}
            </span>
          )
            : (
              <p>
                {isFreeDeliveryAvailable ? t('Product has free shipping')
                  : t('{{price}} shipping per order', { price: `${formatPriceNumber(deliveryCost)} ${currency}` })}
              </p>
            )}
        </div>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  )
}

export default ProductShippingInfo
